
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Clapperboard,
  Computer,
  Megaphone,
  Newspaper,
  BarChart,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Wallet,
  Gamepad2, Earth
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import {Category} from "@/types/Category.ts";



export const Categories = () => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/categories`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    }
  });

  const { data: pollCounts } = useQuery({
    queryKey: ['pollsCount'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/categories/poll-counts`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    }
  });

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, [categories]);

  const getCategoryIcon = (slug: string) => {
    switch (slug) {
      case 'technology':
        return <Computer className="w-4 h-4 mr-2" />;
      case 'sports':
        return <Activity className="w-4 h-4 mr-2" />;
      case 'fun':
        return <Clapperboard className="w-4 h-4 mr-2" />;
      case 'politics':
        return <Megaphone className="w-4 h-4 mr-2" />;
      case 'economy':
        return <BarChart className="w-4 h-4 mr-2" />;
      case 'news':
        return <Newspaper className="w-4 h-4 mr-2" />;
      case 'trending':
        return <TrendingUp className="w-4 h-4 mr-2" />;
      case 'finance':
        return <Wallet className="w-4 h-4 mr-2" />;
      case 'gaming':
        return <Gamepad2 className="w-4 h-4 mr-2" />;
      case 'life':
        return <Earth className="w-4 h-4 mr-2" />;
      default:
        return null;
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-16 bg-white/80 dark:bg-background/80 backdrop-blur-md z-40 border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4">
        {/* Desktop View */}
        <div className="hidden md:block">
          <ScrollArea className="w-full whitespace-nowrap" scrollHideDelay={400}>
            <div className="flex space-x-4 py-4">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-10 w-24 bg-gray-200 dark:bg-gray-800 rounded-md animate-pulse"
                  />
                ))
              ) : (
                categories?.map((category: Category) => (
                  <Button
                    key={category.id}
                    variant="outline"
                    onClick={() => navigate(`/categories/${category.slug}`)}
                    className="flex items-center bg-white dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700 relative"
                  >
                    {getCategoryIcon(category.slug)}
                    {category.name}
                    {pollCounts?.find((x) => x.category_id == category.id) && (
                      <span className="absolute -top-2 -right-2 bg-[#8FC259] dark:bg-[#344F16] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {pollCounts?.find((x) => x.category_id == category.id)?.poll_count}
                      </span>
                    )}
                  </Button>
                ))
              )}
            </div>
            <ScrollBar orientation="horizontal" className="bg-transparent">
              <div className="flex-1 bg-gray-300/20 rounded-full" />
            </ScrollBar>
          </ScrollArea>
        </div>

        {/* Mobile View */}
        <div className="relative md:hidden flex items-center">
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 z-10 h-8 w-8 bg-white dark:bg-gray-800 shadow-sm rounded-full"
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div
            className="overflow-hidden flex-1 px-10"
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            <div className="flex gap-3 py-4">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-9 w-[calc(50%-0.75rem)] shrink-0 bg-gray-200 dark:bg-gray-800 rounded-md animate-pulse"
                  />
                ))
              ) : (
                categories?.map((category: Category) => (
                  <Button
                    key={category.id}
                    variant="outline"
                    onClick={() => navigate(`/categories/${category.slug}`)}
                    className="h-9 px-3 shrink-0 w-[calc(50%-0.75rem)] flex items-center justify-center text-sm bg-white dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                  >
                    {getCategoryIcon(category.slug)}
                    <span className="truncate">{category.name}</span>
                  </Button>
                ))
              )}
            </div>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 z-10 h-8 w-8 bg-white dark:bg-gray-800 shadow-sm rounded-full"
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
